import automationActionApi from "~/api/automation_action";
import automationApi from "~/api/automation";
import automationResultApi from "~/api/automation_result";
import categoryApi from "~/api/category";
import dynamicExtendingSearchApi from "~/api/dynamic_extending_search";
import extendingSearchApi from "~/api/extending_search";
import globalAutomationApi from "~/api/global_automation";
import globalAutomationConfigurationApi from "~/api/global_automation_configuration";
import globalSearchApi from "~/api/global_search";
import searchApi from "~/api/search";
import taskTemplateApi from "~/api/task_template";
import userApi from "~/api/user";
import userDepartmentApi from "~/api/user_department";
import { MainSearchableClassesEnum, RunIntervalEnum } from "../enums";
import keyValueStoreApi from "../../../api/key_value_store";
import automationableApi from "~/api/automationable";

const getName = (context, object) => {
  context.commit("DASHBOARD_NAME_UPDATED", object.name);
};

const fetchAutomationables = ({
  commit,
  state,
}) => {
  commit("LOADING_ACTIVE_AUTOMATIONABLES", true);
  automationableApi
    .paginateActive(1, 200, state.filter)
    .then((response) => {
      commit("LOADING_ACTIVE_AUTOMATIONABLES", false);
      commit("SET_ACTIVE_AUTOMATIONABLES", response.data);
    });
  commit("LOADING_ONCE_AUTOMATIONABLES", true);
  automationableApi
    .paginateOnce(1, 200, state.filter)
    .then((response) => {
      commit("LOADING_ONCE_AUTOMATIONABLES", false);
      commit("SET_ONCE_AUTOMATIONABLES", response.data);
    });
  commit("LOADING_INACTIVE_AUTOMATIONABLES", true);
  automationableApi
    .paginateInactive(1, 200, state.filter)
    .then((response) => {
      commit("LOADING_INACTIVE_AUTOMATIONABLES", false);
      commit("SET_INACTIVE_AUTOMATIONABLES", response.data);
    });
};

const fetchGlobalAutomations = ({
  commit,
  state,
}, reload = false) => {
  if (state.fetchingGlobalAutomations === false
    && (reload === true || state.globalAutomations.length === 0)
  ) {
    commit("SET_FETCHING_GLOBAL_AUTOMATIONS", true);

    return globalAutomationApi
      .paginateWhereHasGlobalAutomationConfiguration(1, 200, state.filter)
      .then((response) => {
        commit("SET_GLOBAL_AUTOMATIONS", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_GLOBAL_AUTOMATIONS", false);
      });
  }

  return Promise.resolve();
};

const fetchAutomations = ({
  commit,
  state,
}, reload = false) => {
  if (state.fetchingAutomations === false && (reload === true || state.automations.length === 0)) {
    commit("SET_FETCHING_AUTOMATIONS", true);

    return automationApi
      .paginate(1, 200, state.filter)
      .then((response) => {
        commit("SET_AUTOMATIONS", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_AUTOMATIONS", false);
      });
  }

  return Promise.resolve();
};

const fetchUnconfiguredGlobalAutomations = ({
  commit,
  state,
}, reload = false) => {
  if (state.fetchingUnconfiguredGlobalAutomations === false
    && (reload === true || state.unconfiguredGlobalAutomations.length === 0)) {
    commit("SET_FETCHING_UNCONFIGURED_GLOBAL_AUTOMATIONS", true);

    return globalAutomationApi
      .paginateWhereWithoutGlobalAutomationConfiguration(1, 200, state.filter)
      .then((response) => {
        commit("SET_UNCONFIGURED_GLOBAL_AUTOMATIONS", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_UNCONFIGURED_GLOBAL_AUTOMATIONS", false);
      });
  }

  return Promise.resolve();
};

const fetchGlobalSearchesByModel = ({
  commit,
  state,
}, model) => {
  if (state.fetchingGlobalSearches === false && state.globalSearches.length === 0) {
    commit("SET_FETCHING_GLOBAL_SEARCHES", true);

    globalSearchApi
      .paginateByModel(model, 1, 200)
      .then((response) => {
        commit("SET_GLOBAL_SEARCHES", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_GLOBAL_SEARCHES", false);
      });
  }

  return Promise.resolve();
};

const fetchSearches = ({
  commit,
  state,
}) => {
  if (state.fetchingSearches === false && state.searches.length === 0) {
    commit("SET_FETCHING_SEARCHES", true);

    searchApi
      .paginate(1, 200)
      .then((response) => {
        commit("SET_SEARCHES", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_SEARCHES", false);
      });
  }

  return Promise.resolve();
};

// Implement the fetchSearches action here
const fetchSearchesByModel = ({
  commit,
  state,
}, model) => {
  if (state.fetchingSearches === false && state.searches.length === 0) {
    commit("SET_FETCHING_SEARCHES", true);

    searchApi
      .paginateByModel(model, 1, 200)
      .then((response) => {
        commit("SET_SEARCHES", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_SEARCHES", false);
      });
  }

  return Promise.resolve();
};

const fetchExtendingSearchesByModel = ({
  commit,
  state,
}, model) => {
  if (state.fetchingExtendingSearches === false && state.extendingSearches.length === 0) {
    commit("SET_FETCHING_EXTENDING_SEARCHES", true);

    extendingSearchApi
      .paginateByModel(model, 1, 200)
      .then((response) => {
        commit("SET_EXTENDING_SEARCHES", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_EXTENDING_SEARCHES", false);
      });
  }

  return Promise.resolve();
};

const fetchAutomationActions = ({
  commit,
  state,
}) => {
  if (state.fetchingAutomationActions === false && state.automationActions.length === 0) {
    commit("SET_FETCHING_AUTOMATION_ACTIONS", true);

    return automationActionApi
      .paginate(1, 200)
      .then((response) => {
        commit("SET_AUTOMATION_ACTIONS", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_AUTOMATION_ACTIONS", false);
      });
  }

  return Promise.resolve();
};

const fetchSearchesByAutomationAction = ({
  commit,
  state,
}, automationAction) => {
  if (state.fetchingSearches === false && state.searches.length === 0) {
    commit("SET_FETCHING_SEARCHES", true);

    searchApi
      .paginateByAutomationAction(automationAction, 1, 200)
      .then((response) => {
        commit("SET_SEARCHES", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_SEARCHES", false);
      });
  }

  return Promise.resolve();
};

const fetchDynamicExtendingSearchesByModel = ({
  commit,
  state,
}, model) => {
  if (state.fetchingDynamicExtendingSearches === false
    && state.dynamicExtendingSearches.length === 0) {
    commit("SET_FETCHING_DYNAMIC_EXTENDING_SEARCHES", true);

    return dynamicExtendingSearchApi
      .paginateByModel(model, 1, 200)
      .then((response) => {
        commit("SET_DYNAMIC_EXTENDING_SEARCHES", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_DYNAMIC_EXTENDING_SEARCHES", false);
      });
  }

  return Promise.resolve();
};

const fetchDynamicExtendingSearchesForSearch = ({
  commit,
  state,
}, searchId) => {
  if (state.fetchingDynamicExtendingSearches === false
    && state.dynamicExtendingSearches.length === 0
    && searchId !== undefined) {
    commit("SET_FETCHING_DYNAMIC_EXTENDING_SEARCHES", true);

    return dynamicExtendingSearchApi
      .paginateBySearch(searchId, 1, 200)
      .then((response) => {
        commit("SET_DYNAMIC_EXTENDING_SEARCHES", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_DYNAMIC_EXTENDING_SEARCHES", false);
      });
  }

  return Promise.resolve();
};

const selectAutomation = (context, automation) => {
  context.commit("SET_SELECTED_AUTOMATION", automation);
};

const selectGlobalAutomation = (context, globalAutomation) => {
  context.commit("SET_SELECTED_GLOBAL_AUTOMATION", globalAutomation);
};

const clearExtendingSearches = (context) => {
  context.commit("SET_EXTENDING_SEARCHES", []);
};

const clearDynamicExtendingSearches = (context) => {
  context.commit("SET_DYNAMIC_EXTENDING_SEARCHES", []);
};

const storeAutomation = (context, automation) => automationApi.store(automation);

const updateAutomation = (context, automation) => automationApi.update(automation);

// implement the disableAutomation action here
const disableAutomation = (context, automation) => automationApi
  .update({
    ...automation,
    run_interval: RunIntervalEnum.NEVER,
  })
  .then((response) => {
    context.commit("SET_SELECTED_AUTOMATION", response.data);

    return response;
  });

const executeOneTimeDataEnrichment = (context, automation) => automationApi
  .executeOneTimeDataEnrichment(automation);

const executeAutomation = (context, automation) => automationApi
  .run(automation.id);

const executeGlobalAutomationConfiguration = (
  context, globalAutomationConfiguration,
) => globalAutomationConfigurationApi.run(globalAutomationConfiguration.id);

const fetchCategories = ({
  commit,
  state,
}) => {
  if (state.fetchingCategories === false && state.categories.length === 0) {
    commit("SET_FETCHING_CATEGORIES", true);

    return categoryApi
      .paginate(1, 200)
      .then((response) => {
        commit("SET_CATEGORIES", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_CATEGORIES", false);
      });
  }

  return Promise.resolve();
};

const fetchUsers = ({
  commit,
  state,
}) => {
  if (state.fetchingUsers === false && state.users.length === 0) {
    commit("SET_FETCHING_USERS", true);

    return userApi
      .paginateActive(1, 200)
      .then((response) => {
        commit("SET_USERS", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_USERS", false);
      });
  }

  return Promise.resolve();
};

const fetchUserDepartments = ({
  commit,
  state,
}) => {
  if (state.fetchingUserDepartments === false && state.userDepartments.length === 0) {
    commit("SET_FETCHING_USER_DEPARTMENTS", true);

    return userDepartmentApi
      .paginate(1, 200)
      .then((response) => {
        commit("SET_USER_DEPARTMENTS", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_USER_DEPARTMENTS", false);
      });
  }

  return Promise.resolve();
};

const fetchTaskTemplates = ({
  commit,
  state,
}) => {
  if (state.fetchingTaskTemplates === false && state.taskTemplates.length === 0) {
    commit("SET_FETCHING_TASK_TEMPLATES", true);

    return taskTemplateApi.get(["users", "departments", "product_labels"])
      .then((response) => {
        commit("SET_TASK_TEMPLATES", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_TASK_TEMPLATES", false);
      });
  }

  return Promise.resolve();
};

const fetchMe = ({
  commit,
  state,
}) => {
  if (state.fetchingMe === false && state.me === null) {
    commit("SET_FETCHING_ME", true);

    return userApi.getMe()
      .then((response) => {
        commit("SET_ME", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_ME", false);
      });
  }

  return Promise.resolve();
};

const findOrCreateGlobalAutomationConfiguration = (
  context,
  globalAutomationConfigurationId,
) => globalAutomationConfigurationApi.getById(globalAutomationConfigurationId)
  .then((response) => response.data);

const getAutomationActionById = (
  context,
  automationActionId,
) => automationActionApi.getById(automationActionId)
  .then((response) => response.data);

const findOrCreateGlobalAutomationConfigurationByGlobalAutomation = (
  context,
  globalAutomationId,
) => globalAutomationConfigurationApi.findOrCreateByGlobalAutomation(globalAutomationId)
  .then((response) => response.data);

const getTypeDescriptionByModel = (context, model) => {
  if (model === MainSearchableClassesEnum.CUSTOMER) { return "Klanten"; }
  if (model === MainSearchableClassesEnum.POLICY) { return "Polissen"; }
  if (model === MainSearchableClassesEnum.DAMAGE) { return "Schades"; }
  if (model === MainSearchableClassesEnum.MORTGAGE) { return "Hypotheken"; }
  if (model === MainSearchableClassesEnum.MORTGAGE_PART) { return "Leningdelen"; }
  if (model === MainSearchableClassesEnum.OBLIGATION) { return "Kredieten"; }
  if (model === MainSearchableClassesEnum.TASK) { return "Taken"; }
  return null;
};

const updateGlobalAutomationConfiguration = (
  context, globalAutomationConfiguration,
) => globalAutomationConfigurationApi.update(globalAutomationConfiguration);

const selectGlobalAutomationConfiguration = (context, globalAutomationConfiguration) => {
  context.commit("SET_SELECTED_GLOBAL_AUTOMATION_CONFIGURATION", globalAutomationConfiguration);
};

const disableGlobalAutomationConfiguration = (
  context, globalAutomationConfiguration,
) => globalAutomationConfigurationApi.update({
  ...globalAutomationConfiguration,
  run_interval: RunIntervalEnum.NEVER,
});

const setAutomationResults = (context, automationResults) => {
  context.commit("SET_AUTOMATION_RESULTS", automationResults);
};

const fetchAutomationResults = ({
  commit,
  state,
}, { page, perPage }) => {
  if (state.fetchingAutomationResults === false) {
    commit("SET_AUTOMATION_RESULTS", []); // can be removed when grouped by automation and global automation configuration
    commit("SET_FETCHING_AUTOMATION_RESULTS", true);

    return automationResultApi
      .paginate(page, perPage)
      .then((response) => {
        commit("SET_AUTOMATION_RESULTS_PAGINATION", response.meta?.pagination);
        commit("SET_AUTOMATION_RESULTS", response.data);
      })
      .finally(() => {
        commit("SET_FETCHING_AUTOMATION_RESULTS", false);
      });
  }

  return Promise.resolve();
};

const paginateUnprocessedResultsByAutomation = ({
  commit,
}, {
  automation, page, perPage, name,
}) => automationResultApi
  .paginateUnprocessedByAutomation(
    automation.id,
    page,
    perPage,
    name,
  ).then((response) => {
    commit("SET_AUTOMATION_RESULTS_PAGINATION", response.meta?.pagination);
    commit("SET_AUTOMATION_RESULTS", response.data);
  })
  .finally(() => {
    commit("SET_FETCHING_AUTOMATION_RESULTS", false);
  });

const paginateUnprocessedResultsByGlobalAutomationConfiguration = ({
  commit,
}, {
  globalAutomationConfiguration, page, perPage, name,
}) => automationResultApi
  .paginateUnprocessedByGlobalAutomationConfiguration(
    globalAutomationConfiguration.id,
    page,
    perPage,
    name,
  ).then((response) => {
    commit("SET_AUTOMATION_RESULTS_PAGINATION", response.meta?.pagination);
    commit("SET_AUTOMATION_RESULTS", response.data);
  })
  .finally(() => {
    commit("SET_FETCHING_AUTOMATION_RESULTS", false);
  });

const selectAutomationResult = ({
  commit,
  state,
}, automationResult) => {
  const automationResults = state.selectedAutomationResults;

  if (automationResults.find((ar) => ar.id === automationResult.id) === undefined) {
    automationResults.push(automationResult);
  }

  commit("SET_SELECTED_AUTOMATION_RESULTS", automationResults);
};

const deselectAutomationResult = ({
  commit,
  state,
}, automationResult) => {
  let automationResults = state.selectedAutomationResults;

  automationResults = automationResults.filter((ar) => ar.id !== automationResult.id);

  commit("SET_SELECTED_AUTOMATION_RESULTS", automationResults);
};

const isAutomationResultSelected = ({
  state,
}, automationResult) => state.selectedAutomationResults
  .find((ar) => ar.id === automationResult.id) !== undefined;

const manuallyProcessAutomationResult = ({
  commit,
  state,
}, automationResult) => automationResultApi
  .processByUser(automationResult.id, state.me.id)
  .then((response) => {
    commit("SET_AUTOMATION_RESULT_AS_PROCESSED", automationResult);
    return response;
  });

const ignoreAutomationResult = ({
  commit,
  state,
}, automationResult) => automationResultApi
  .ignoreByUser(automationResult.id, state.me.id)
  .then((response) => {
    commit("SET_AUTOMATION_RESULT_AS_IGNORED", response.data);
    return response;
  });

const processUnprocessedByAutomation = ({
  state,
}, id) => automationResultApi
  .processUnprocessedByAutomation(id, state.me?.id)
  .then((response) => response.data);

const manuallyProcessSelectedAutomationResults = ({
  commit,
  state,
}) => automationResultApi
  .processAllByUser(state.selectedAutomationResults)
  .then((response) => {
    commit("SET_SELECTED_AUTOMATION_RESULTS", []);

    return response;
  });

const ignoreSelectedAutomationResults = ({
  commit,
  state,
}) => automationResultApi
  .ignoreAllByUser(state.ignoredAutomationResults, state.me?.id)
  .then((response) => {
    commit("SET_SELECTED_AUTOMATION_RESULTS", []);
    commit("SET_IGNORED_AUTOMATION_RESULTS", []);

    return response;
  });

const saveFilter = ({ state }) => keyValueStoreApi.set("automation_index", state.filter);

const getFilter = ({ commit, dispatch, state }) => {
  commit("SET_FILTER_LOADED", false);
  keyValueStoreApi.has("automation_index").then((hasFilter) => {
    if (hasFilter) {
      keyValueStoreApi.get("automation_index").then((response) => {
        commit("SET_FILTER_LOADED", true);
        commit("SET_FILTER", response);
      });
    } else {
      keyValueStoreApi.set("automation_index", state.defaultFilter).then(() => {
        commit("SET_FILTER_LOADED", true);
      });
    }
  }).catch(() => {
    dispatch("saveDisplayState");
  });
};

const setSelectedSortingCategory = ({ commit }, category) => {
  commit("SET_SELECTED_SORTING_CATEGORY", category);
};
const setShowEmailAutomations = ({ commit }, checked) => {
  commit("SET_SHOW_EMAIL_AUTOMATIONS", checked);
};
const setShowSignalAutomations = ({ commit }, checked) => {
  commit("SET_SHOW_SIGNAL_AUTOMATIONS", checked);
};
const setShowTaskAutomations = ({ commit }, checked) => {
  commit("SET_SHOW_TASK_AUTOMATIONS", checked);
};
const setSearchQuery = ({ commit }, query) => {
  commit("SET_SEARCH_QUERY", query);
};

export default {
  saveFilter,
  getFilter,
  findOrCreateGlobalAutomationConfiguration,
  getAutomationActionById,
  fetchUnconfiguredGlobalAutomations,
  clearDynamicExtendingSearches,
  clearExtendingSearches,
  disableAutomation,
  disableGlobalAutomationConfiguration,
  executeOneTimeDataEnrichment,
  executeAutomation,
  executeGlobalAutomationConfiguration,
  fetchAutomationables,
  fetchAutomationActions,
  fetchAutomationResults,
  fetchAutomations,
  fetchCategories,
  fetchDynamicExtendingSearchesByModel,
  fetchExtendingSearchesByModel,
  fetchGlobalAutomations,
  fetchGlobalSearchesByModel,
  fetchMe,
  fetchSearches,
  fetchSearchesByAutomationAction,
  fetchSearchesByModel,
  fetchTaskTemplates,
  fetchUserDepartments,
  fetchUsers,
  paginateUnprocessedResultsByGlobalAutomationConfiguration,
  paginateUnprocessedResultsByAutomation,
  findOrCreateGlobalAutomationConfigurationByGlobalAutomation,
  getName,
  getTypeDescriptionByModel,
  selectAutomation,
  selectGlobalAutomation,
  selectGlobalAutomationConfiguration,
  setAutomationResults,
  storeAutomation,
  updateAutomation,
  updateGlobalAutomationConfiguration,
  selectAutomationResult,
  deselectAutomationResult,
  isAutomationResultSelected,
  manuallyProcessAutomationResult,
  ignoreAutomationResult,
  manuallyProcessSelectedAutomationResults,
  ignoreSelectedAutomationResults,
  processUnprocessedByAutomation,
  fetchDynamicExtendingSearchesForSearch,
  setSelectedSortingCategory,
  setShowEmailAutomations,
  setShowSignalAutomations,
  setShowTaskAutomations,
  setSearchQuery,
};
